import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
} from "react";

/* -----------------Third parties--------------- */
import {
	BottomSheetBackdrop,
	type BottomSheetBackdropProps,
	BottomSheetModal,
	type BottomSheetModalProps,
	BottomSheetScrollView,
	type BottomSheetScrollViewMethods,
} from "@gorhom/bottom-sheet";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { useAppTheme } from "../../useAppTheme";
/* -----------------Hooks--------------- */
import Box from "../Box";
import SheetHandle from "../SheetHandle";

export type CustomHandle = (
	handleExpandCollapse: () => void,
) => React.ReactNode;

type Props = Omit<
	BottomSheetModalProps,
	"handleComponent" | "snapPoints" | "index"
> & {
	children: React.ReactNode;
	showModal?: boolean;
	snapIndex: number;
	handleSetSnapIndex?: (index: number) => void;
	customHandle?: CustomHandle;
	header?: React.ReactNode;
	backdropPressBehavior?: React.ComponentProps<
		typeof BottomSheetBackdrop
	>["pressBehavior"];
} & (
		| {
				snapPoint: number | string;
				snapPoints?: never;
		  }
		| {
				snapPoints: (number | string)[];
				snapPoint?: never;
		  }
	);

const BottomSheetModalWrapper = forwardRef<BottomSheetModal, Props>(
	(props, ref) => {
		const {
			children,
			showModal,
			snapPoint,
			snapPoints: snapPointsProp,
			handleSetSnapIndex,
			customHandle: CustomHandle,
			animateOnMount = false,
			enableDismissOnClose = false,
			enablePanDownToClose = false,
			snapIndex = 0,
			style,
			backgroundStyle,
			onChange,
			header,
			backdropPressBehavior = "close",
			...rest
		} = props;

		const theme = useAppTheme();
		const insets = useSafeAreaInsets();
		const scrollViewRef = useRef<BottomSheetScrollViewMethods>(null);
		const currentSnapIndexRef = useRef(snapIndex);
		const snapPoints = useMemo(
			() => snapPointsProp || [snapPoint, "100%"],
			[snapPoint, snapPointsProp],
		);

		const renderBackdrop = useCallback(
			(props: BottomSheetBackdropProps) => (
				<BottomSheetBackdrop {...props} pressBehavior={backdropPressBehavior} />
			),
			[backdropPressBehavior],
		);

		const innerBottomSheetRef = useRef<BottomSheetModal>(null);
		useImperativeHandle(ref, () => innerBottomSheetRef.current!, []);

		const handleSheetChange = useCallback(
			(index: number) => {
				handleSetSnapIndex?.(index);
				if (index === 0) {
					scrollViewRef.current?.scrollTo({ x: 0, y: 0, animated: true });
				}
				currentSnapIndexRef.current = index;
				onChange?.(index, 0, 0);
			},
			[handleSetSnapIndex, onChange],
		);

		useEffect(() => {
			if (showModal) {
				innerBottomSheetRef.current?.present();
			} else {
				innerBottomSheetRef.current?.dismiss();
			}
		}, [showModal]);

		const handleHandlePress = useCallback(() => {
			try {
				if (innerBottomSheetRef.current) {
					if (currentSnapIndexRef.current === 0) {
						innerBottomSheetRef.current.snapToIndex(1);
					} else {
						innerBottomSheetRef.current.snapToIndex(0);
					}
				}
			} catch (_e) {
				// This means that there is only 1 snap point
				innerBottomSheetRef.current?.dismiss();
			}
		}, []);

		const Handle = useCallback(() => {
			if (CustomHandle) {
				return CustomHandle(handleHandlePress);
			}
			return <SheetHandle onPress={handleHandlePress} />;
		}, [handleHandlePress, CustomHandle]);

		if (!showModal) {
			return null;
		}

		return (
			<BottomSheetModal
				ref={innerBottomSheetRef}
				style={[
					{
						shadowColor: theme.colors.shadow,
						shadowOffset: {
							width: 0,
							height: 0,
						},
						shadowOpacity: 1,
						shadowRadius: 8,
						elevation: 8,
						borderTopLeftRadius: 25,
						borderTopRightRadius: 25,
					},
					style,
				]}
				backgroundStyle={[
					{
						borderStyle: "solid",
						borderWidth: 2,
						borderColor: theme.colors.divider,
						borderTopLeftRadius: 25,
						borderTopRightRadius: 25,
						backgroundColor: theme.colors.elevation.level1,
					},
					backgroundStyle,
				]}
				animateOnMount={animateOnMount}
				enableDismissOnClose={enableDismissOnClose}
				enablePanDownToClose={enablePanDownToClose}
				index={snapIndex}
				snapPoints={snapPoints}
				topInset={insets.top + 10}
				onChange={handleSheetChange}
				handleComponent={Handle}
				backdropComponent={renderBackdrop}
				{...rest}
			>
				{!!header && <Box paddingHorizontal={16}>{header}</Box>}
				<BottomSheetScrollView
					contentContainerStyle={{
						padding: 16,
						paddingTop: 0,
						paddingBottom: 80,
						flexGrow: 1,
					}}
					ref={scrollViewRef}
				>
					{children}
				</BottomSheetScrollView>
			</BottomSheetModal>
		);
	},
);
export default BottomSheetModalWrapper;
